import { create } from 'zustand';
import { setAuthToken } from '../api/client';
import { authApi, diceApi } from '../api/dice';
import { telegram } from '../lib/telegram';

export type Screen =
  | 'dice'
  | 'crash'
  | 'roulette'
  | 'slots'
  | 'top'
  | 'profile'
  | 'fairness'
  | 'referral'
  | 'transfer'
  | 'stats';

export const GAME_SCREENS: Screen[] = ['dice', 'crash', 'roulette', 'slots'];
export type Game = 'dice' | 'crash' | 'roulette' | 'slots';

interface User {
  id: number;
  telegramId: number;
  username?: string;
}

interface ToastState {
  msg: string;
  danger?: boolean;
  key: number;
}

export interface FeedItem {
  id: string;
  name: string;
  game: 'dice' | 'roulette';
  stake: string;
  payout: string;
  multiplier: number;
  win: boolean;
  detail: string;
  ts: number;
}

interface AppStore {
  authLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  balance: string;
  screen: Screen;
  lastGame: Game;
  toast: ToastState | null;
  verifyBetId: number | null;
  liveFeed: FeedItem[];
  bigWin: FeedItem | null;

  authenticate: (initData: string) => Promise<void>;
  devLogin: (profileId?: string) => Promise<void>;
  restoreSession: () => boolean;
  logout: () => void;
  setBalance: (b: string) => void;
  refreshBalance: () => Promise<void>;
  setScreen: (s: Screen) => void;
  openVerify: (betId: number) => void;
  showToast: (msg: string, danger?: boolean) => void;
  pushFeed: (item: FeedItem) => void;
  triggerBigWin: (item: FeedItem) => void;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
let bigWinTimer: ReturnType<typeof setTimeout> | null = null;

const SESSION_KEY = 'raketta_session';
function saveSession(accessToken: string, user: User) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ accessToken, user }));
  } catch {
    /* приватный режим */
  }
}
export function clearSession() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export const useAppStore = create<AppStore>((set, get) => ({
  authLoading: true,
  isAuthenticated: false,
  user: null,
  balance: '0',
  screen: 'dice',
  lastGame: 'dice',
  toast: null,
  verifyBetId: null,
  liveFeed: [],
  bigWin: null,

  authenticate: async (initData: string) => {
    try {
      const data = await authApi.login(initData);
      setAuthToken(data.accessToken);
      saveSession(data.accessToken, data.user);
      set({ isAuthenticated: true, user: data.user, authLoading: false });
      get().refreshBalance();
    } catch (e) {
      console.error('Авторизация провалилась', e);
      set({ authLoading: false });
    }
  },

  devLogin: async (profileId?: string) => {
    try {
      set({ authLoading: true });
      const data = await authApi.devLogin(profileId);
      setAuthToken(data.accessToken);
      saveSession(data.accessToken, data.user);
      set({ isAuthenticated: true, user: data.user, authLoading: false });
      get().refreshBalance();
    } catch (e) {
      console.error('Dev-вход не удался', e);
      set({ authLoading: false });
    }
  },

  // Мгновенное восстановление сессии из localStorage (без сетевого запроса).
  restoreSession: () => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return false;
      const { accessToken, user } = JSON.parse(raw) as { accessToken: string; user: User };
      if (!accessToken || !user) return false;
      setAuthToken(accessToken);
      set({ isAuthenticated: true, user, authLoading: false });
      get().refreshBalance();
      return true;
    } catch {
      return false;
    }
  },

  logout: () => {
    setAuthToken(null);
    clearSession();
    set({ isAuthenticated: false, user: null, balance: '0' });
  },

  setBalance: (b: string) => set({ balance: b }),

  refreshBalance: async () => {
    try {
      const { amount } = await diceApi.balance();
      set({ balance: amount });
    } catch (e) {
      console.error('❌ Не смогли получить баланс', e);
    }
  },

  setScreen: (s: Screen) =>
    set({
      screen: s,
      lastGame: GAME_SCREENS.includes(s) ? (s as Game) : get().lastGame,
      verifyBetId: s === 'fairness' ? get().verifyBetId : null,
    }),

  openVerify: (betId: number) => set({ verifyBetId: betId, screen: 'fairness' }),

  showToast: (msg: string, danger?: boolean) => {
    if (toastTimer) clearTimeout(toastTimer);
    set({ toast: { msg, danger, key: Date.now() } });
    telegram.haptic(danger ? 'rigid' : 'light');
    toastTimer = setTimeout(() => set({ toast: null }), 2600);
  },

  pushFeed: (item: FeedItem) =>
    set((s) => ({ liveFeed: [item, ...s.liveFeed].slice(0, 30) })),

  triggerBigWin: (item: FeedItem) => {
    if (bigWinTimer) clearTimeout(bigWinTimer);
    set({ bigWin: item });
    bigWinTimer = setTimeout(() => set({ bigWin: null }), 6000);
  },
}));
